import { lazy, Suspense } from 'react';
import StagedLoader, { type LoaderStep } from '@/components/StagedLoader';
import AppLoadingScreen from '@/components/AppLoadingScreen';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@context/AuthContext';
import Main from '@pages/Main';
import Landing from '@pages/Landing';
import { PaymentSuccess } from '@pages/PaymentSuccess';
import { ProfilePage } from '@pages/ProfilePage';
import PatentLandingPage from '@pages/PatentLandingPage';
import PatentFullTextDisplay from '@pages/PatentFullTextDisplay';
import DraftsList from '@pages/DraftsList';
import DraftEditor from '@pages/DraftEditor';
import { IDSPage } from '@/components/IDSPage';
import { ResolutionWarningModal } from '@/components/ResolutionWarningModal';
import { isInfringementHost } from '@/infringement/host';

// Lazy so the docket bundle never ships to regular PatentLoop users.
const DocketApp = lazy(() => import('@/docket/DocketApp'));
// Same for ForTech — a separate section most users never open.
const ForTechApp = lazy(() => import('@/fortech/ForTechApp'));
// Same for Drafting — the /drafting application builder, self-contained under src/drafting/.
const DraftingApp = lazy(() => import('@/drafting/DraftingApp'));
// And for Action — the /action Office action responder, self-contained under src/action/.
const ActionApp = lazy(() => import('@/action/ActionApp'));
// Same for Cases — Federal Circuit case-law search, self-contained under src/cases/.
const CasesApp = lazy(() => import('@/cases/CasesApp'));
// Admin user management: only admins ever load it, so keep it out of everyone's bundle.
const AdminUsersPage = lazy(() => import('@pages/AdminUsersPage'));
const InvoicesPage = lazy(() => import('@pages/InvoicesPage'));
// The infringement finder: its own site on infringement.patentloop.com, sharing only the
// bundle, the login and the backend. Never loaded on app.patentloop.com.
const InfringementApp = lazy(() => import('@/infringement/InfringementApp'));

const DRAFTING_CHUNK_STEPS: LoaderStep[] = [
  { label: 'Loading the drafting tool', ms: 2500 },
  { label: 'Opening your applications' },
];

// Protected Route component
function ProtectedRoute({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <AppLoadingScreen />;
  }

  return user ? children : <Navigate to="/login" replace />;
}

// Landing Route component (redirect to app if already logged in)
function LandingRoute() {
  const { user, loading, setUser, checkAuth } = useAuth();

  if (loading) {
    return <AppLoadingScreen />;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleLoginSuccess = (token: string, username: string) => {
    console.log('Login successful, updating auth state:', username);
    // Token and username are already stored in localStorage by the API service
    // Call checkAuth to fetch account info from backend immediately
    checkAuth();
  };

  return <Landing onLoginSuccess={handleLoginSuccess} />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LandingRoute />} />
      <Route path="/success" element={<PaymentSuccess />} />
      <Route path="/patent" element={<PatentLandingPage />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      {/* Admin-only, same guard: the page asks the backend before rendering anything. */}
      <Route
        path="/admin/invoices"
        element={
          <ProtectedRoute>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center text-slate-500">
                  Loading invoicing…
                </div>
              }
            >
              <InvoicesPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Admin-only; the page itself asks the backend whether the caller may manage users. */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center text-slate-500">
                  Loading admin…
                </div>
              }
            >
              <AdminUsersPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drafts"
        element={
          <ProtectedRoute>
            <DraftsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drafts/:draftId"
        element={
          <ProtectedRoute>
            <DraftEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ids"
        element={
          <ProtectedRoute>
            <IDSPage />
          </ProtectedRoute>
        }
      />
      {/* Must precede /:docNumber, which would otherwise swallow /docket. */}
      <Route
        path="/docket/*"
        element={
          <ProtectedRoute>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center text-slate-500">
                  Loading docket…
                </div>
              }
            >
              <DocketApp />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* Must also precede /:docNumber, which would otherwise swallow /fortech. */}
      <Route
        path="/fortech/*"
        element={
          <ProtectedRoute>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center text-slate-500">
                  Loading ForTech…
                </div>
              }
            >
              <ForTechApp />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* Must also precede /:docNumber, which would otherwise swallow /cases. */}
      <Route
        path="/cases/*"
        element={
          <ProtectedRoute>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center text-slate-500">
                  Loading Cases…
                </div>
              }
            >
              <CasesApp />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* Must also precede /:docNumber, which would otherwise swallow /drafting. */}
      <Route
        path="/drafting/*"
        element={
          <ProtectedRoute>
            <Suspense
              fallback={
                /* The drafting bundle is a large chunk, so a cold cache sits here for a
                   moment with nothing else on screen. `appearAfter` keeps a warm cache —
                   the common case — from flashing a loader it does not need. */
                <StagedLoader
                  title="Loading drafting…"
                  steps={DRAFTING_CHUNK_STEPS}
                  appearAfter={250}
                />
              }
            >
              <DraftingApp />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* Must also precede /:docNumber, which would otherwise swallow /action. */}
      <Route
        path="/action/*"
        element={
          <ProtectedRoute>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center text-slate-500">
                  Loading office action…
                </div>
              }
            >
              <ActionApp />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/:docNumber"
        element={<PatentFullTextDisplay />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Main />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * The routes on infringement.patentloop.com: the shared login, and the finder. None of
 * PatentLoop's own pages are reachable there, and the finder has no route on PatentLoop.
 */
function InfringementRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LandingRoute />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center text-slate-500">
                  Loading Infringement Finder…
                </div>
              }
            >
              <InfringementApp />
            </Suspense>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  if (isInfringementHost()) {
    return (
      <AuthProvider>
        <InfringementRoutes />
      </AuthProvider>
    );
  }
  return (
    <AuthProvider>
      <ResolutionWarningModal />
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
